/**
 * **运行**这条线在宿主里的落点：谁触发、跑哪条链、结果留给谁看。
 *
 * ## 它把三件事接起来
 *
 * ```
 *   ① 能力检查   requirementOf(图里用到的节点类型) ↔ 当前宿主
 *                  → 缺什么就**拒绝执行并说清**（不静默降级）
 *   ② 触发范围   "手动触发"节点 = 这次的 targets → 只跑它们的下游闭包
 *   ③ 串行 + 历史 连点两下不并发（排队）；每次运行留一条记录（结果视图要用）
 * ```
 *
 * ⚠️ **为什么能力检查放在跑之前**：文档里那条规矩是"生产端没有人看着，
 * 缺能力必须拒绝执行"（`print-template-architecture.md` §2.8）。
 * 跑到一半才发现"这个宿主没有文件系统"是最坏的形态 —— 前面的副作用已经发生了。
 *
 * ⚠️ **为什么历史不是"当前状态"**：输出窗口里那份诊断是**派生**的
 * （修好的自己消失，§`useProblems` 的说明）。运行记录正好相反 ——
 * 它是**发生过的**事，不该因为图改了而消失。所以两者分开存，
 * 也不放在同一个列表里（那是两份语义不同的东西）。
 */
import { computed, ref, type ComputedRef, type Ref } from 'vue'
import type { GraphEdge, NodeEvaluator } from '@/node/evaluate'
import type { NodeRunner, RunNodeResult, RunResult } from '@/node/runGraph'
import { createRunController, type RunRecord } from '@/node/runGraph'
import type { GraphNodeModel } from '@/types/node'
import type { NodeTypeDef } from '@/node/package'
import { requirementOf } from '@/node/package'
import {
  BROWSER_HOST,
  describeHostCheck,
  hostCanRun,
  type HostCapability,
  type HostDescriptor,
  type TriggerKind,
} from '@/types/capability'

export interface UseRunsContext {
  /** 当前图的节点（求值投影，和设计期读到的是同一份） */
  models: () => readonly GraphNodeModel[]
  edges: () => readonly GraphEdge[]
  /** 纯求值器（与设计期**同一批**） */
  evaluators: () => Record<string, NodeEvaluator>
  /** 副作用执行器（包贡献） */
  runners: () => Record<string, NodeRunner>
  /** 图里用到的节点类型定义（能力检查按它算） */
  nodeTypes: () => readonly NodeTypeDef[]
  /** 当前宿主（默认浏览器） */
  host?: () => HostDescriptor
  /** 图变了要让什么重算（宿主传自己的版本号；本 composable 只读它一下） */
  version?: () => unknown
}

export interface UseRuns {
  /** 运行记录（最近的在前） */
  history: Ref<RunRecord[]>
  /** 最近一次（结果视图显示它） */
  last: ComputedRef<RunRecord | null>
  /** 现在在干什么（按钮的三种形态） */
  phase: ComputedRef<'idle' | 'running' | 'queued'>
  /** 排队中的数量 */
  queued: Ref<number>
  /** 这次能跑吗（不能就跑不了 + 原因；界面上要显示出来） */
  gate: ComputedRef<{ ok: boolean; reason: string; missing: readonly string[] }>
  /** 跑一次：`targets` = 触发节点；不给就跑整张图 */
  run: (targets?: readonly string[]) => Promise<RunRecord>
  /** 取消正在跑的那次 */
  cancel: () => void
}

export function useRuns(ctx: UseRunsContext): UseRuns {
  const history = ref<RunRecord[]>([])
  const queued = ref(0)
  const running = ref(false)
  const last = computed<RunRecord | null>(() => history.value[0] ?? null)
  const phase = computed<'idle' | 'running' | 'queued'>(() => {
    if (running.value) return 'running'
    return queued.value > 0 ? 'queued' : 'idle'
  })
  const controller = createRunController()

  /**
   * **能力闸门**：这份图需要什么 ↔ 这个宿主给什么。
   *
   * ⚠️ 它是**只读**的派生值（图一变就重算），所以界面上可以一直显示
   * "能不能跑" —— 而不是等用户点了才开始检查。
   */
  const gate = computed(() => {
    ctx.version?.()
    const host = ctx.host?.() ?? BROWSER_HOST
    const used = new Set(ctx.models().map((model) => model.type))
    const requirement = requirementOf(ctx.nodeTypes().filter((def) => used.has(def.type)))
    const check = hostCanRun(host, requirement)
    return {
      ok: check.ok,
      reason: describeHostCheck(host, requirement),
      missing: [...check.missingCapabilities, ...check.missingTriggers] as (
        | HostCapability
        | TriggerKind
      )[],
    }
  })

  async function run(targets?: readonly string[]): Promise<RunRecord> {
    const startedAt = Date.now()
    const by = [...(targets ?? [])]
    /*
     * ⚠️ 能力不足**在跑之前**就拒绝：跑到一半才发现"这个宿主没有文件系统"
     * 是最坏的形态 —— 前面的副作用已经发生了。
     */
    if (!gate.value.ok) {
      const record: RunRecord = {
        seq: history.value.length + 1,
        startedAt,
        finishedAt: Date.now(),
        durationMs: 0,
        by,
        cancelled: false,
        failure: gate.value.reason,
        nodes: [],
        outputs: new Map(),
        sinks: [],
      }
      history.value = [record, ...history.value].slice(0, 50)
      return record
    }

    queued.value += 1
    try {
      const result: RunResult = await controller.request({
        models: ctx.models(),
        edges: ctx.edges(),
        evaluators: ctx.evaluators(),
        runners: ctx.runners(),
        ...(targets && targets.length > 0 ? { targets } : {}),
      })
      const failed = result.nodes.find((item) => item.status === 'failed')
      const record: RunRecord = {
        seq: history.value.length + 1,
        startedAt: result.startedAt,
        finishedAt: result.finishedAt,
        durationMs: result.finishedAt - result.startedAt,
        by,
        cancelled: result.cancelled,
        failure: failed ? `${failed.type}：${failed.error ?? '失败'}` : '',
        nodes: result.nodes,
        outputs: result.outputs,
        sinks: result.sinks,
      }
      history.value = [record, ...history.value].slice(0, 50)
      return record
    } finally {
      queued.value = Math.max(0, queued.value - 1)
    }
  }

  return {
    history,
    last,
    phase,
    queued,
    gate,
    run,
    cancel: () => controller.cancel(),
  }
}
